import React from 'react';
import { useMemo, useState } from 'react';
import { useTheme, Box, Tooltip } from '@mui/material';
import * as d3 from "d3";
import { Word as D3Word } from 'd3-cloud';

import WordCloudTooltip from './WordCloudTooltip';
import { Query } from 'system/query/Query';
import { QueryInfo } from 'system/query/QueryInfo';
import { DashboardQueryDrilldownDialog } from 'components/dialogs/QueryDrilldown/DashboardQueryDrilldownDialog';
import { WordCloudSettings } from './WordCloudSettings';
import WidgetAlert, { WidgetErrorType } from 'components/widgetParts/WidgetAlert';
import ReusableWordCloud from './ReusableWordCloud';

export interface WordCloudContentProps {
  activeQueryIds?: string[];
  apiResponses: any;
  contentRef?: any;
  isDrilldown?: boolean;
  queryInfo?: QueryInfo;
  widget?: any;
  wordCloudSettings?: any;
  seed?: number;
}

export interface Word {
  cluster_id: string,
  label: string,
  language: string,
  relevance?: number,
  distribution: number,
  count: number,
  est_total_count: number,
  appearances: number,
  est_total_appearances?: number,
  negative_count: number,
  positive_count: number,
  est_total_negative_count?: number,
  est_total_positive_count?: number,
  positivity: number,
  sentimental: number,
  entity?: object,
  timeline?: Array<number>,
  timeline_distribution?: Array<number>,
  velocity: number|undefined,
  relationships?: WordRelationship[],
  query: any,
  size?: number
}

export interface WordRelationship {
  cooccurrence: number,
  index: number,
  label: string,
  similarity: number
}

export interface CloudWord extends D3Word {
  text: string,
  displayText: string,
  value: number,
  color: string,
  size: number,
  width?: number,
  height?: number,
  meta: {
    entity?: object,
    positivity: number,
    relevance?: number,
    count: number,
    distribution: number,
    velocity: number | undefined,
    query: Query
  }
}

export default function WordCloudContent(props: WordCloudContentProps): JSX.Element {
  const wordCloudSettings = props.wordCloudSettings as WordCloudSettings;

  const [hoveredNode, setHoveredNode] = useState<CloudWord | null>(null);
  const [selectedNode, setSelectedNode] = useState<CloudWord | null>(null);
  let isDrilldown = props.isDrilldown ?? false;

  const firstApiResponse = props.apiResponses?.topics ?? [];

  const data = firstApiResponse ?? [];
  const dataTypes = props.widget?.request_info?.body?.topics?.types ?? ["topic"];
  const dataKey = wordCloudSettings?.dataKey ?? "relevance";
  const colorKey = wordCloudSettings?.colorKey ?? "positivity";

  const isIconType = (dataTypes.length === 1 && (dataTypes[0] === "flag" || dataTypes[0] === "emoji")) || (dataTypes.length === 2 && dataTypes.includes("flag") && dataTypes.includes("emoji"));

  const fontRange = !isIconType ? [16, 100] : [32, 200];
  const sizeRange = useMemo(() => {
    const values = firstApiResponse?.map((data: Word) => data[dataKey]).filter((value: number) => value !== undefined) || [];
    return [Math.min(...values), Math.max(...values)];
  }, [firstApiResponse, dataKey]);
  
  const colorRange = useMemo(() => {
    if(colorKey === 'positivity') {
      // Sentiment positivity is a special-case color range, and should always use 0-1, not the data min/max
      return [0.0, 1.0];
    }
    const values = firstApiResponse?.map((data: Word) => data[colorKey]).filter((value: number) => value !== undefined) || [];
    return [Math.min(...values), Math.max(...values)];
  }, [firstApiResponse, colorKey]);

  const sizeScale = d3.scaleLinear().domain(sizeRange).range(fontRange);
  const colorScale = d3.scaleLinear().domain(colorRange).range([0.0, 1.0]);
  
  const theme = useTheme();
  const namedColors = theme.colors.namedColors;
  const colorInterpolate = colorKey === 'positivity' ?
    d3.interpolateRgbBasis([namedColors.negative, namedColors.mixed, namedColors.positive]) :
    d3.interpolateRgbBasis([namedColors.chart1Light, namedColors.chart1, namedColors.chart1Dark]);

  // Handle word interactivity if word cloud uses it - Used with <ReusableWordCloud />
  const handleOnMouseEnter = (node: CloudWord) => setHoveredNode(node);
  const handleOnMouseLeave = () => setHoveredNode(null);
  const handleSetSelectedNode = (node: CloudWord) => setSelectedNode(node);

  // Prepares the API data for D3Cloud() processing.
  const words = useMemo(() => {
    if (data.length === 0) { return; }

    // Combines the API data with the D3Cloud() data structure and adding other necessary properties to pass through
    const combinedWords = data.map((data: Word): CloudWord => {

      return {
        text: !isIconType ? data.label : "🌑", // Use Circular icon for layout. Allows for better icon spacing/rendering
        displayText: data.label,
        value: data[dataKey],
        size: sizeScale(data[dataKey]),
        color: data[colorKey] === null ? namedColors.neutral : colorInterpolate(colorScale(data[colorKey])),
        meta: {
          entity: data.entity,
          positivity: data.positivity,
          count: data.est_total_count,
          relevance: data.relevance,
          distribution: data.distribution,
          velocity: data.velocity,
          query: data.query
        }
      };
    });

    // Trim the list to the maximum number of words
    combinedWords.slice(0, wordCloudSettings?.wordCount ?? 100);

    return combinedWords;
  }, [data]);

  return (
    <>
      {data.length !== 0 ? (
        <Tooltip
          title={hoveredNode ? <WordCloudTooltip topic={hoveredNode} /> : ''}
          open={Boolean(hoveredNode)}
          followCursor={true}
        >
          <Box sx={{ width: "100%", height: "100%" }}>
            <ReusableWordCloud
              contentRef={props.contentRef}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
              onWordClick={handleSetSelectedNode}
              seed={props.seed}
              settings={wordCloudSettings}
              words={words} />

            {selectedNode && !isDrilldown &&
              <DashboardQueryDrilldownDialog
                title={selectedNode.displayText ?? ""}
                queryInfo={props.queryInfo}
                onClose={() => setSelectedNode(null)}
                activeQueryIds={props.activeQueryIds}
                drilldownQuery={new Query({ query: selectedNode.meta.query })}
                widgetRequestInfo={props.widget?.request_info}
              />
            }
          </Box>
        </Tooltip>
      ) : (
        <WidgetAlert type={WidgetErrorType.NO_DATA} />
      )}
    </>
  );
}
